import { useState, useRef, useCallback, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'

const ITEM_HEIGHT = 36
const COLUMN_HEIGHT = 216

export interface PickerColumnProps {
    options: string[]
    value: string
    onChange: (val: string) => void
    testIdPrefix?: string
    'data-testid'?: string
}

// Wheel tuning — keep stepping calm regardless of input device speed.
const WHEEL_DELTA_THRESHOLD = 24 // px of wheel travel before we step 1 item
const WHEEL_LOCK_MS = 90 // min spacing between consecutive steps

const toTestIdValue = (value: string, index: number) => {
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

    return normalized || `option-${index}`
}

export const PickerColumn = ({ options, value, onChange, testIdPrefix = 'picker-column', 'data-testid': testId = 'picker-column' }: PickerColumnProps) => {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const isMovingRef = useRef(false)
    const startYRef = useRef(0)
    const startTranslateRef = useRef(0)
    const wheelAccumRef = useRef(0)
    const wheelLockRef = useRef(false)
    const [translate, setTranslate] = useState(0)

    const maxTranslate = COLUMN_HEIGHT / 2 - ITEM_HEIGHT / 2
    const minTranslate = COLUMN_HEIGHT / 2 - ITEM_HEIGHT * options.length + ITEM_HEIGHT / 2

    const computeTranslate = useCallback(
        (val: string) => {
            const idx = options.indexOf(val)
            return idx >= 0 ? maxTranslate - idx * ITEM_HEIGHT : maxTranslate
        },
        [options, maxTranslate]
    )

    useEffect(() => {
        setTranslate(computeTranslate(value))
    }, [value, computeTranslate])

    const snap = useCallback(
        (t: number) => {
            let idx: number
            if (t > maxTranslate) idx = 0
            else if (t < minTranslate) idx = options.length - 1
            else idx = -Math.floor((t - maxTranslate) / ITEM_HEIGHT)
            idx = Math.max(0, Math.min(idx, options.length - 1))
            setTranslate(maxTranslate - idx * ITEM_HEIGHT)
            const selected = options[idx]
            if (selected !== undefined) onChange(selected)
        },
        [maxTranslate, minTranslate, options, onChange]
    )

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0]
        if (!touch) return
        isMovingRef.current = true
        startYRef.current = touch.clientY
        startTranslateRef.current = translate
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isMovingRef.current) return
        const touch = e.touches[0]
        if (!touch) return
        const diff = touch.clientY - startYRef.current
        let next = startTranslateRef.current + diff
        if (next > maxTranslate) {
            next = maxTranslate + Math.pow(next - maxTranslate, 0.8)
        } else if (next < minTranslate) {
            next = minTranslate - Math.pow(minTranslate - next, 0.8)
        }
        setTranslate(next)
    }

    const handleTouchEnd = () => {
        if (!isMovingRef.current) return
        isMovingRef.current = false
        snap(translate)
    }

    const handleWheel = (e: React.WheelEvent) => {
        e.stopPropagation()

        // Accumulate small trackpad deltas instead of stepping on every event.
        wheelAccumRef.current += e.deltaY

        if (wheelLockRef.current) return
        if (Math.abs(wheelAccumRef.current) < WHEEL_DELTA_THRESHOLD) return

        const direction = wheelAccumRef.current > 0 ? -1 : 1
        wheelAccumRef.current = 0
        wheelLockRef.current = true
        window.setTimeout(() => {
            wheelLockRef.current = false
        }, WHEEL_LOCK_MS)

        snap(translate + direction * ITEM_HEIGHT)
    }

    const handleClick = (opt: string) => {
        setTranslate(computeTranslate(opt))
        onChange(opt)
    }

    return (
        <Column
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onWheel={handleWheel}
            data-testid={`${testId}-column`}>
            <Scroller ref={scrollerRef} style={{ transform: `translate3d(0, ${translate}px, 0)` }} data-testid={`${testId}-scroller`}>
                {options.map((opt, index) => (
                    <Item
                        key={opt}
                        className={opt === value ? 'selected' : ''}
                        onClick={() => handleClick(opt)}
                        data-testid={`${testIdPrefix}-${toTestIdValue(opt, index)}-option`}>
                        {opt}
                    </Item>
                ))}
            </Scroller>
            <Highlight data-testid={`${testId}-highlight`} />
        </Column>
    )
}

export { ITEM_HEIGHT as PICKER_ITEM_HEIGHT, COLUMN_HEIGHT as PICKER_COLUMN_HEIGHT }

export const PickerContainer = styled(Box)`
    display: flex;
    height: ${COLUMN_HEIGHT}px;
    position: relative;
    user-select: none;
    overflow: hidden;
`

const Column = styled(Box)`
    flex: 1;
    position: relative;
    overflow: hidden;
    text-align: center;
    cursor: pointer;

    @media (pointer: coarse) {
        cursor: default;
    }
`

const Scroller = styled('div')`
    transition: transform 300ms ease-out;
`

const Item = styled('div')`
    height: ${ITEM_HEIGHT}px;
    line-height: ${ITEM_HEIGHT}px;
    font-family: 'Poppins', sans-serif;
    font-size: 1.2em;
    color: #999999;

    &.selected {
        color: #000000;
        font-weight: bold;
    }
`

const Highlight = styled('div')`
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: ${ITEM_HEIGHT}px;
    margin-top: -${ITEM_HEIGHT / 2}px;
    pointer-events: none;

    &::before,
    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        background: #d9d9d9;
    }

    &::before {
        top: 0;
    }
    &::after {
        bottom: 0;
    }
`
